import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { api, fileUrl } from '@/lib/api';
import { toast } from 'sonner';
import {
  CalendarClock, PackageCheck, PackageX, RefreshCcw, Truck, Wallet,
  Ban, XCircle, MessageSquareText, ShieldCheck, Camera, Loader2, X,
  CheckCircle2, Send,
} from 'lucide-react';

const SECTIONS = [
  {
    icon: CalendarClock,
    title: '7-Day Return Window',
    body: (
      <>
        <p>Customers may request a return within <strong>7 calendar days</strong> after the order is delivered.</p>
        <p>To request a return, you must contact us within the 7-day period. Returns sent without prior authorization may not be accepted.</p>
      </>
    ),
  },
  {
    icon: PackageCheck,
    title: 'Return Eligibility',
    body: (
      <>
        <p>To qualify for a return, the product must generally:</p>
        <ul>
          <li>Be unused, unworn, and in substantially the same condition in which it was received.</li>
          <li>Include its original packaging, accessories, manuals, and other included components when applicable.</li>
          <li>Include proof of purchase or the applicable order number.</li>
        </ul>
        <p>Products that have been used, damaged after delivery, altered, washed, installed, or otherwise made unsuitable for resale may not qualify for a return.</p>
      </>
    ),
  },
  {
    icon: PackageX,
    title: 'Damaged, Defective, Incorrect or Missing Items',
    body: (
      <>
        <p>Please inspect your order when it arrives.</p>
        <p>If your product arrives damaged, defective, incorrect, or with items missing, contact us as soon as possible. We may request photographs or video showing the product, packaging, shipping label, damage, defect, or missing components.</p>
        <p>Once verified, we may provide an appropriate resolution, which can include a replacement, reshipment, partial refund, or full refund, depending on the circumstances.</p>
      </>
    ),
  },
  {
    icon: RefreshCcw,
    title: 'Change-of-Mind Returns',
    body: (
      <>
        <p>If you simply change your mind, order the wrong item, select the wrong size/color/variant, or no longer want the product, contact us within <strong>7 days</strong> of delivery.</p>
        <p>Approval of these returns is subject to the item's condition and eligibility.</p>
        <p>Unless otherwise required by law or stated by us, customers are responsible for return shipping costs for change-of-mind returns.</p>
      </>
    ),
  },
  {
    icon: Truck,
    title: 'Return Shipping',
    body: (
      <>
        <p><strong>Do not return a product to the address shown on the original shipping package</strong> unless we specifically instruct you to do so.</p>
        <p>Because products may be fulfilled from different warehouses, an approved return address will be provided after your return request has been reviewed.</p>
        <p>For verified damaged, defective, or incorrect products, we will provide instructions regarding the appropriate resolution and any applicable return shipping.</p>
      </>
    ),
  },
  {
    icon: Wallet,
    title: 'Refunds',
    body: (
      <>
        <p>After an approved return is received and inspected, we will notify you of the status of your refund.</p>
        <p>Approved refunds will generally be issued to the original payment method. Processing times after a refund is issued can vary depending on your bank, credit card issuer, or payment provider.</p>
        <p>Original shipping charges, when separately charged, may be non-refundable except when required by law or when the return results from an error on our part.</p>
      </>
    ),
  },
  {
    icon: Ban,
    title: 'Non-Returnable Items',
    body: (
      <>
        <p>Certain products may not be eligible for return for health, hygiene, safety, customization, or other reasons. These may include:</p>
        <ul>
          <li>Personalized or custom-made products</li>
          <li>Used or installed products</li>
          <li>Personal-care or hygiene products once opened, where applicable</li>
          <li>Products damaged through misuse or improper handling</li>
          <li>Gift cards or other non-returnable digital products, if offered</li>
          <li>Other products specifically identified as final sale or non-returnable before purchase</li>
        </ul>
        <p>Your statutory rights, where applicable, are not affected.</p>
      </>
    ),
  },
  {
    icon: XCircle,
    title: 'Order Cancellations',
    body: (
      <>
        <p>We process orders as quickly as possible. If you need to cancel an order, contact us immediately.</p>
        <p>We cannot guarantee cancellation after an order has entered processing, fulfillment, or shipment.</p>
      </>
    ),
  },
  {
    icon: MessageSquareText,
    title: 'How to Request a Return',
    body: (
      <>
        <p>To start a return or report a problem with an order, contact Royal Palm through the customer-support contact information provided on our website.</p>
        <p>Please provide:</p>
        <ul>
          <li>Order number</li>
          <li>Customer name/email</li>
          <li>Product involved</li>
          <li>Reason for the request</li>
          <li>Photos or video when reporting damage, defects, incorrect products, or missing items</li>
        </ul>
        <p>We will review the request and provide further instructions.</p>
      </>
    ),
  },
];

export default function ReturnsPage() {
  return (
    <Layout>
      {/* Hero */}
      <section className="bg-[#F7F9F8] border-b border-[#E6E9EE]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#C8922F] font-semibold">
            <ShieldCheck className="h-4 w-4" /> Royal Palm Store
          </div>
          <h1 data-testid="returns-page-title" className="mt-3 font-headline text-4xl md:text-5xl text-[#17352D] tracking-[-0.02em]">
            Return & Refund Policy
          </h1>
          <p className="mt-3 text-sm text-slate-500">Last Updated: August 20, 2026</p>
          <p className="mt-4 text-slate-700 max-w-2xl">
            We want you to be satisfied with your purchase. Please review the following return and refund terms before placing an order.
          </p>
        </div>
      </section>

      {/* Policy sections */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        <div className="space-y-6">
          {SECTIONS.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="rounded-2xl border border-[#E6E9EE] bg-white p-6 md:p-8"
              data-testid={`returns-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="h-10 w-10 rounded-xl bg-[#F2F7F5] text-[#17352D] flex items-center justify-center">
                  <Icon className="h-5 w-5" />
                </span>
                <h2 className="font-headline text-xl md:text-2xl text-[#17352D]">{title}</h2>
              </div>
              <div className="policy-prose text-[15px] leading-relaxed text-slate-700 space-y-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_strong]:text-[#17352D]">
                {body}
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-10 rounded-2xl bg-[#17352D] text-white p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="font-headline text-xl">Need to start a return?</div>
            <p className="text-sm text-white/70 mt-1">Fill out the request form below — our team will review it and send you the next steps.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => document.getElementById('return-request-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="rounded-xl bg-[#C8922F] hover:bg-[#B27F26] text-white"
              data-testid="returns-start-request-btn"
            >
              Start Return Request
            </Button>
            <Button asChild variant="outline" className="rounded-xl border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="returns-shop-btn">
              <Link to="/shop">Back to Shop</Link>
            </Button>
          </div>
        </div>

        {/* Return Request Form */}
        <ReturnRequestForm />
      </section>
    </Layout>
  );
}

const REASONS = [
  { value: 'damaged', label: 'Arrived damaged' },
  { value: 'defective', label: 'Defective / not working' },
  { value: 'incorrect_item', label: 'Incorrect item received' },
  { value: 'missing_items', label: 'Items missing' },
  { value: 'change_of_mind', label: 'Changed my mind' },
  { value: 'other', label: 'Other' },
];

function ReturnRequestForm() {
  const [form, setForm] = useState({ order_number: '', name: '', email: '', product: '', reason: '', details: '' });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // {reference}
  const fileRef = useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 8 - photos.length);
    e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        if (!f.type.startsWith('image/')) { toast.error(`"${f.name}" is not an image`); continue; }
        if (f.size > 10 * 1024 * 1024) { toast.error(`"${f.name}" is too large (max 10MB)`); continue; }
        const fd = new FormData();
        fd.append('file', f);
        const r = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setPhotos(p => [...p, r.data.url]);
      }
    } catch (err) {
      toast.error('Photo upload failed — please try again');
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.order_number.trim()) { toast.error('Please enter your order number'); return; }
    if (!form.name.trim()) { toast.error('Please enter your name'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { toast.error('Please enter a valid email'); return; }
    if (!form.product.trim()) { toast.error('Please tell us which product is involved'); return; }
    if (!form.reason) { toast.error('Please select a reason'); return; }
    const needsPhotos = ['damaged', 'defective', 'incorrect_item', 'missing_items'].includes(form.reason);
    if (needsPhotos && photos.length === 0) {
      toast.error('Please add at least one photo showing the issue');
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post('/returns', { ...form, photos });
      setDone({ reference: r.data.reference });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit your request — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div id="return-request-form" className="mt-10 rounded-2xl border border-[#E6E9EE] bg-[#F7F9F8] p-8 md:p-10 text-center" data-testid="return-request-success">
        <CheckCircle2 className="h-12 w-12 text-[#17352D] mx-auto mb-3" />
        <h2 className="font-headline text-2xl text-[#17352D]">Request received — #{done.reference}</h2>
        <p className="mt-2 text-slate-600 max-w-lg mx-auto">
          We've emailed you a confirmation. Our team will review your request and reply with the next steps.
          Please don't ship anything back until you receive an approved return address.
        </p>
        <Button asChild className="mt-5 rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white">
          <Link to="/shop" data-testid="return-success-shop-btn">Back to Shop</Link>
        </Button>
      </div>
    );
  }

  return (
    <div id="return-request-form" className="mt-10 rounded-2xl border border-[#E6E9EE] bg-white p-6 md:p-8" data-testid="return-request-form">
      <div className="flex items-center gap-3 mb-1">
        <span className="h-10 w-10 rounded-xl bg-[#F2F7F5] text-[#17352D] flex items-center justify-center">
          <Send className="h-5 w-5" />
        </span>
        <h2 className="font-headline text-xl md:text-2xl text-[#17352D]">Start a Return Request</h2>
      </div>
      <p className="text-sm text-slate-600 mb-5">Submit the details below — we'll review your request and email you the next steps within the 7-day window.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Order number *</Label>
          <Input value={form.order_number} onChange={e => set('order_number', e.target.value)} placeholder="e.g. RP-10234" className="rounded-xl mt-1 h-11" data-testid="return-form-order" />
        </div>
        <div>
          <Label className="text-xs">Your name *</Label>
          <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Full name" className="rounded-xl mt-1 h-11" data-testid="return-form-name" />
        </div>
        <div>
          <Label className="text-xs">Email *</Label>
          <Input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="you@email.com" className="rounded-xl mt-1 h-11" data-testid="return-form-email" />
        </div>
        <div>
          <Label className="text-xs">Reason *</Label>
          <Select value={form.reason} onValueChange={v => set('reason', v)}>
            <SelectTrigger className="rounded-xl mt-1 h-11" data-testid="return-form-reason">
              <SelectValue placeholder="Select a reason" />
            </SelectTrigger>
            <SelectContent>
              {REASONS.map(r => (
                <SelectItem key={r.value} value={r.value} data-testid={`return-reason-${r.value}`}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Product involved *</Label>
          <Input value={form.product} onChange={e => set('product', e.target.value)} placeholder="Product name from your order" className="rounded-xl mt-1 h-11" data-testid="return-form-product" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Details (optional)</Label>
          <Textarea rows={4} value={form.details} onChange={e => set('details', e.target.value)} placeholder="Tell us what happened…" className="rounded-xl mt-1" data-testid="return-form-details" />
        </div>
      </div>

      {/* Photos */}
      <div className="mt-4">
        <Label className="text-xs">Photos {['damaged', 'defective', 'incorrect_item', 'missing_items'].includes(form.reason) ? '(required for this reason)' : '(optional)'}</Label>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onPickPhotos} />
        <div className="flex flex-wrap gap-2 mt-2">
          {photos.map((u, i) => (
            <div key={u} className="relative rounded-xl overflow-hidden border border-slate-200 h-20 w-20" data-testid={`return-photo-${i}`}>
              <img src={fileUrl(u)} alt={`Photo ${i + 1}`} className="h-full w-full object-cover" />
              <button type="button" onClick={() => setPhotos(p => p.filter(x => x !== u))} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80" aria-label="Remove photo">
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          {photos.length < 8 && (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="h-20 w-20 rounded-xl border-2 border-dashed border-slate-300 text-slate-500 hover:border-[#C8922F] hover:text-[#C8922F] flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors"
              data-testid="return-form-upload"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              {uploading ? 'Uploading…' : 'Add photos'}
            </button>
          )}
        </div>
      </div>

      <Button onClick={submit} disabled={submitting || uploading} className="mt-6 rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white h-11 px-6" data-testid="return-form-submit">
        {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</> : <><Send className="h-4 w-4 mr-2" /> Submit Return Request</>}
      </Button>
    </div>
  );
}
